import React, { useEffect, useState } from 'react';
import { Button, Checkbox, FormControl, Grid, ListItemText, MenuItem, Popover, Select, SelectChangeEvent, Stack, Typography } from '@mui/material';
import { Box } from '@mui/system';
import Filter from '../../../../icons/filter.svg';
import { useDispatch, useSelector } from 'react-redux';
import { ExtendedWorkOrderStatusEnum, resetWorkOrderSearchFilters, selectWorkOrderSearchFitlers, setWorkOrderSearchFilters } from '../../sagas/workorderSlice';
import WorkOrdersTableHeaderTextInput from './WorkOrdersTableHeaderTextInput';
import { selectCurrentUser } from '../../../../shared/sagas/auth/authSlice';
import { Role } from '@inosio/payments-sdk';
import { selectPmConfigSettingsState } from '../../../configSettings/sagas/pmConfigSettingsSlice';
import { selectVendorsState } from '../../../vendors/sagas/vendorsSlice';

interface AssigneeType {
  Uuid: string;
  Name: string;
}

const WorkOrdersTableHeader = () => {
  const dispatch = useDispatch();

  //New status filter: start
  const statusFilterOptions = [
    { key: ExtendedWorkOrderStatusEnum.NeedsReview, label: 'Needs Review' },
    { key: ExtendedWorkOrderStatusEnum.GroupedInProgress, label: 'In Progress' },
    { key: ExtendedWorkOrderStatusEnum.OwnerDiscussion, label: 'Owner Discussion', parentKey: ExtendedWorkOrderStatusEnum.GroupedInProgress },
    { key: ExtendedWorkOrderStatusEnum.ObtainingQuotes, label: 'Obtaining Quotes', parentKey: ExtendedWorkOrderStatusEnum.GroupedInProgress },
    { key: ExtendedWorkOrderStatusEnum.VendorDiscussion, label: 'Vendor Discussion', parentKey: ExtendedWorkOrderStatusEnum.GroupedInProgress },
    { key: ExtendedWorkOrderStatusEnum.SentToVendor, label: 'Sent to Vendor', parentKey: ExtendedWorkOrderStatusEnum.GroupedInProgress },
    { key: ExtendedWorkOrderStatusEnum.WorkInProgress, label: 'Work in Progress', parentKey: ExtendedWorkOrderStatusEnum.GroupedInProgress },
    { key: ExtendedWorkOrderStatusEnum.GroupedCompleted, label: 'Completed' },
    { key: ExtendedWorkOrderStatusEnum.Completed, label: 'Completed', parentKey: ExtendedWorkOrderStatusEnum.GroupedCompleted },
    { key: ExtendedWorkOrderStatusEnum.InvoiceReceived, label: 'Invoice Received', parentKey: ExtendedWorkOrderStatusEnum.GroupedCompleted },
  ];

  //selectors
  const workOrderSearchFitlers = useSelector(selectWorkOrderSearchFitlers);
  const { selectedStatusOptions, assigneeUuids, Title, WorkOrderId, PropertyName, ResidentName } =
    workOrderSearchFitlers;
  const { associatedCompanyStaff } = useSelector(selectPmConfigSettingsState);
  const { Vendors } = useSelector(selectVendorsState);
  const currentUser = useSelector(selectCurrentUser);
  const isVendorUser = currentUser?.currentProfile.role === Role.Vendor;
  const isMaintenanceStaffUser = currentUser?.currentProfile.role === Role.MaintenanceStaff;

  const [isDefaultWorkOrderStatusFilter,setIsDefaultWorkOrderStatusFilter] = useState<boolean>(true)
  const [allAssignees, setAllAssignees] = useState<AssigneeType[]>([]);
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
  const [workOrderTitleFilter, setWorkOrderTitleFilter] = useState<string>(Title);
  const [workOrderIdFilter, setWorkOrderIdFilter] = useState<string>(WorkOrderId);
  const [workOrderPropertyNameFilter, setWorkOrderProperyNameFilter] = useState<string>(PropertyName);
  const [workOrderResidentNameFilter, setWorkOrderResidentNameFilter] = useState<string>(ResidentName);
  const [workOrderStatusFilter, setWorkOrderStatusFilter] = useState<ExtendedWorkOrderStatusEnum[]>(selectedStatusOptions);
  const [workOrderAssignedToFilter, setWorkOrderAssignedToFilter] = useState<string[]>(assigneeUuids);

  // Boolean to check if the popover is open
  const isPopOverOpen = Boolean(anchorEl);

  useEffect(() => {
    if (associatedCompanyStaff.staff?.length) {
      const staff = associatedCompanyStaff.staff.map((s) => {
        return {
          Uuid: s.Uuid,
          Name: `${s.FirstName} ${s.LastName}`,
        } as AssigneeType;
      });
      setAllAssignees(staff);
    }
    if (Vendors?.Rows?.length) {
      const vendors = Vendors?.Rows?.map((v) => {
        return {
          Uuid: v.Uuid,
          Name: v.Name,
        } as AssigneeType;
      });
      setAllAssignees((value) => [...value, ...vendors]);
    }
  }, [associatedCompanyStaff, Vendors]);

  // Function to handle opening the popover
  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    setAnchorEl(event.currentTarget); // Set the clicked element as the anchor
  };

  // Function to handle closing the popover
  const handleClose = () => {
    setAnchorEl(null);
  };

  //resets all filter values
  const handleClearAllFilters = () => {
    //reset state values
    setWorkOrderIdFilter('');
    setWorkOrderTitleFilter('');
    setWorkOrderProperyNameFilter('');
    setWorkOrderResidentNameFilter('');
    setWorkOrderAssignedToFilter([]);
    setWorkOrderStatusFilter([ExtendedWorkOrderStatusEnum.NeedsReview]);

    dispatch(resetWorkOrderSearchFilters());
    setIsDefaultWorkOrderStatusFilter(true)
    //close the modal
    handleClose();
  };

  // Handle changes in the workorder title input in filters
  const handleChangeWorkOrderTitleFilter = (event: React.ChangeEvent<HTMLInputElement>) => {
    setWorkOrderTitleFilter(event.target.value);
  };

  // Handle changes in the workorder title input in filters
  const handleChangeWorkOrderIdFilter = (event: React.ChangeEvent<HTMLInputElement>) => {
    setWorkOrderIdFilter(event.target.value);
  };

  // Handle changes in the workorder title input in filters
  const handleChangeWorkOrderPropertyFilter = (event: React.ChangeEvent<HTMLInputElement>) => {
    setWorkOrderProperyNameFilter(event.target.value);
  };

  // Handle changes in the workorder title input in filters
  const handleChangeWorkOrderResidentFilter = (event: React.ChangeEvent<HTMLInputElement>) => {
    setWorkOrderResidentNameFilter(event.target.value);
  };

  //So complicated Status Filter handler (I hate my solution but just keep it unless it fails)
  const handleChangeStatusFilter = (event: SelectChangeEvent<typeof workOrderStatusFilter>) => {
    const {
      target: { value },
    } = event;

    let options = value as ExtendedWorkOrderStatusEnum[];

    //Checked In Progress
    if (workOrderStatusFilter.indexOf(ExtendedWorkOrderStatusEnum.GroupedInProgress) === -1 && options.indexOf(ExtendedWorkOrderStatusEnum.GroupedInProgress) > -1) {
      if (options.indexOf(ExtendedWorkOrderStatusEnum.OwnerDiscussion) === -1) options.push(ExtendedWorkOrderStatusEnum.OwnerDiscussion);
      if (options.indexOf(ExtendedWorkOrderStatusEnum.ObtainingQuotes) === -1) options.push(ExtendedWorkOrderStatusEnum.ObtainingQuotes);
      if (options.indexOf(ExtendedWorkOrderStatusEnum.VendorDiscussion) === -1) options.push(ExtendedWorkOrderStatusEnum.VendorDiscussion);
      if (options.indexOf(ExtendedWorkOrderStatusEnum.SentToVendor) === -1) options.push(ExtendedWorkOrderStatusEnum.SentToVendor);
      if (options.indexOf(ExtendedWorkOrderStatusEnum.WorkInProgress) === -1) options.push(ExtendedWorkOrderStatusEnum.WorkInProgress);
    }
    //Unchecked In Progress
    else if (workOrderStatusFilter.indexOf(ExtendedWorkOrderStatusEnum.GroupedInProgress) > -1 && options.indexOf(ExtendedWorkOrderStatusEnum.GroupedInProgress) === -1) {
      options = options.filter(
        (option) =>
          ![
            ExtendedWorkOrderStatusEnum.OwnerDiscussion,
            ExtendedWorkOrderStatusEnum.ObtainingQuotes,
            ExtendedWorkOrderStatusEnum.VendorDiscussion,
            ExtendedWorkOrderStatusEnum.SentToVendor,
            ExtendedWorkOrderStatusEnum.WorkInProgress,
          ].includes(option),
      );
    }
    //Checked all of In Progress options
    else if (
      options.indexOf(ExtendedWorkOrderStatusEnum.GroupedInProgress) === -1 &&
      options.indexOf(ExtendedWorkOrderStatusEnum.OwnerDiscussion) > -1 &&
      options.indexOf(ExtendedWorkOrderStatusEnum.ObtainingQuotes) > -1 &&
      options.indexOf(ExtendedWorkOrderStatusEnum.VendorDiscussion) > -1 &&
      options.indexOf(ExtendedWorkOrderStatusEnum.SentToVendor) > -1 &&
      options.indexOf(ExtendedWorkOrderStatusEnum.WorkInProgress) > -1
    ) {
      options.push(ExtendedWorkOrderStatusEnum.GroupedInProgress);
    }
    //Unchecked one of In Progress options
    else if (
      options.indexOf(ExtendedWorkOrderStatusEnum.GroupedInProgress) > -1 &&
      (options.indexOf(ExtendedWorkOrderStatusEnum.OwnerDiscussion) === -1 ||
        options.indexOf(ExtendedWorkOrderStatusEnum.ObtainingQuotes) === -1 ||
        options.indexOf(ExtendedWorkOrderStatusEnum.VendorDiscussion) === -1 ||
        options.indexOf(ExtendedWorkOrderStatusEnum.SentToVendor) === -1 ||
        options.indexOf(ExtendedWorkOrderStatusEnum.WorkInProgress) === -1)
    ) {
      options = options.filter((option) => option !== ExtendedWorkOrderStatusEnum.GroupedInProgress);
    }
    //Checked Grouped Completed
    else if (workOrderStatusFilter.indexOf(ExtendedWorkOrderStatusEnum.GroupedCompleted) === -1 && options.indexOf(ExtendedWorkOrderStatusEnum.GroupedCompleted) > -1) {
      if (options.indexOf(ExtendedWorkOrderStatusEnum.Completed) === -1) options.push(ExtendedWorkOrderStatusEnum.Completed);
      if (options.indexOf(ExtendedWorkOrderStatusEnum.InvoiceReceived) === -1) options.push(ExtendedWorkOrderStatusEnum.InvoiceReceived);
    }
    //Unchecked Grouped Completed
    else if (workOrderStatusFilter.indexOf(ExtendedWorkOrderStatusEnum.GroupedCompleted) > -1 && options.indexOf(ExtendedWorkOrderStatusEnum.GroupedCompleted) === -1) {
      options = options.filter((option) => ![ExtendedWorkOrderStatusEnum.Completed, ExtendedWorkOrderStatusEnum.InvoiceReceived].includes(option));
    }
    //Checked all of Grouped Completed options
    else if (
      options.indexOf(ExtendedWorkOrderStatusEnum.GroupedCompleted) === -1 &&
      options.indexOf(ExtendedWorkOrderStatusEnum.Completed) > -1 &&
      options.indexOf(ExtendedWorkOrderStatusEnum.InvoiceReceived) > -1
    ) {
      options.push(ExtendedWorkOrderStatusEnum.GroupedCompleted);
    }
    //Unchecked one of Grouped Completed options
    else if (
      options.indexOf(ExtendedWorkOrderStatusEnum.GroupedCompleted) > -1 &&
      (options.indexOf(ExtendedWorkOrderStatusEnum.Completed) === -1 || options.indexOf(ExtendedWorkOrderStatusEnum.InvoiceReceived) === -1)
    ) {
      options = options.filter((option) => option !== ExtendedWorkOrderStatusEnum.GroupedCompleted);
    }

    setWorkOrderStatusFilter(options);
    setIsDefaultWorkOrderStatusFilter(false)
    
  };

  const handleChangeAssigneeFilter = (event: SelectChangeEvent<typeof workOrderAssignedToFilter>) => {
    const {
      target: { value },
    } = event;
    setWorkOrderAssignedToFilter(typeof value === 'string' ? value.split(',') : value);
  };

  const handleSearch = () => {
    dispatch(
      setWorkOrderSearchFilters({
        recordStartIndex: 0,
        assigneeUuids: workOrderAssignedToFilter,
        selectedStatusOptions: isDefaultWorkOrderStatusFilter ?  getDefaultStatusToAllSelected(): workOrderStatusFilter,
        Title: workOrderTitleFilter,
        WorkOrderId: workOrderIdFilter,
        PropertyName : workOrderPropertyNameFilter,
        ResidentName : workOrderResidentNameFilter
      }),
    );
    handleClose();
  };

  const getDefaultStatusToAllSelected = () => {
    const allSelectedStatusFilter = statusFilterOptions.map((option) => option.key)
    setWorkOrderStatusFilter(allSelectedStatusFilter)
    return allSelectedStatusFilter
  }

  // listens for Enter key press and invokes the handleSearch method
  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (event.key === 'Enter') {
        handleSearch();
      }
    };
    // Attach keydown event listener only when the popover is open
    if (isPopOverOpen) {
      window.addEventListener('keydown', handleKeyPress);
    }

    // Cleanup event listener on component unmount to prevent memory leaks
    return () => {
      window.removeEventListener('keydown', handleKeyPress);
    };
  }, [handleSearch]);

  return (
    <Box sx={{ display: 'flex' }}>
      <Box sx={{ display: 'flex', cursor: 'pointer', alignItems: 'center', marginBottom: '6px' }} onClick={handleClick}>
        <img src={Filter} style={{ height: '25px', width: '25px' }} alt="Filter Icon" />
        <Typography sx={{ fontFamily: 'Work Sans', fontStyle: 'normal', fontWeight: 700, marginLeft: 2 }}>Search and Filter:</Typography>
      </Box>

      <Popover
        open={isPopOverOpen}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'top', // Align the top of the popover with the top of the anchor element
          horizontal: 'left', // Align the left of the popover with the left of the anchor element
        }}
        transformOrigin={{
          vertical: 'top', // Align the top of the popover to the top of the anchor
          horizontal: 'left', // Align the left of the popover to the left of the anchor
        }}
        PaperProps={{
          sx: {
            borderRadius: '25px',
            padding: 2,
            width: '700px',
            ml: -2,
            mt: -2,
          },
        }}
      >
        <Grid container spacing={2}>
          <Grid item xs={6} sx={{ display: 'flex', cursor: 'pointer' }} onClick={handleClose}>
            <img src={Filter} style={{ height: '25px', width: '25px' }} alt="Filter Icon" />
            <Typography sx={{ fontFamily: 'Work Sans', fontStyle: 'normal', fontWeight: 700, marginLeft: 2 }}>Search and Filter:</Typography>
          </Grid>
          <Grid item xs={6} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button sx={{ textTransform: 'none' }} onClick={handleClearAllFilters}>
              Clear All
            </Button>
          </Grid>

          {/* Title Field */}
          <Grid item xs={12} sm={3} sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography sx={{ fontFamily: 'Work Sans', fontWeight: 700 }}>Title:</Typography>
          </Grid>
          <Grid item xs={12} sm={7}>
            <WorkOrdersTableHeaderTextInput name="Title" value={workOrderTitleFilter} onChange={handleChangeWorkOrderTitleFilter} autoFocus={true} />
          </Grid>

          {/* Work Order ID Field */}
          <Grid item xs={12} sm={3} sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography sx={{ fontFamily: 'Work Sans', fontWeight: 700 }}>Work Order ID:</Typography>
          </Grid>
          <Grid item xs={12} sm={7}>
            <WorkOrdersTableHeaderTextInput name="WorkOrderId" value={workOrderIdFilter} onChange={handleChangeWorkOrderIdFilter} />
          </Grid>

          {/* Work Order Property Field */}
          <Grid item xs={12} sm={3} sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography sx={{ fontFamily: 'Work Sans', fontWeight: 700 }}>Property:</Typography>
          </Grid>
          <Grid item xs={12} sm={7}>
            <WorkOrdersTableHeaderTextInput name="Property" value={workOrderPropertyNameFilter} onChange={handleChangeWorkOrderPropertyFilter} />
          </Grid>

          {/* Work Order Resident Field */}
          <Grid item xs={12} sm={3} sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography sx={{ fontFamily: 'Work Sans', fontWeight: 700 }}>Resident:</Typography>
          </Grid>
          <Grid item xs={12} sm={7}>
            <WorkOrdersTableHeaderTextInput name="Resident" value={workOrderResidentNameFilter} onChange={handleChangeWorkOrderResidentFilter} />
          </Grid>

          {/* Work Order Assigned To Field */}
          <Grid item xs={12} sm={3} sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography sx={{ fontFamily: 'Work Sans', fontWeight: 700 }}>Assigned To:</Typography>
          </Grid>

          {!isVendorUser &&!isMaintenanceStaffUser &&    <Grid item xs={12} sm={7}>
            <FormControl sx={{ m: 1, minWidth: 190, maxWidth: 300 }}>
              <Select
                multiple
                displayEmpty
                value={workOrderAssignedToFilter}
                onChange={handleChangeAssigneeFilter}
                renderValue={(uuids) => {
                  if (uuids.length === 0) {
                    return 'Select Assigned to';
                  }
                  const names = allAssignees.filter((a) => uuids.includes(a.Uuid)).map((a) => a.Name);
                  return names.join(', ');
                }}
                inputProps={{ 'aria-label': 'Without label' }}
                style={{ borderRadius: '24px', height: '42px' }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      borderRadius: '24px',
                    },
                  },
                }}
              >
                {allAssignees.map((assignee) => (
                  <MenuItem key={assignee.Uuid} value={assignee.Uuid}>
                    <Checkbox checked={workOrderAssignedToFilter.indexOf(assignee.Uuid) > -1} />
                    <ListItemText primary={assignee.Name} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>}
        

          {/* Work Order Status Field */}
          <Grid item xs={12} sm={3} sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography sx={{ fontFamily: 'Work Sans', fontWeight: 700 }}>Status:</Typography>
          </Grid>
          <Grid item xs={12} sm={7}>
            <FormControl sx={{ m: 1, minWidth: 190, maxWidth: 300 }}>
              <Select
                multiple
                displayEmpty
                value={workOrderStatusFilter}
                onChange={handleChangeStatusFilter}
                renderValue={(keys) => {
                  if (keys.length === 0) {
                    return 'Select Status';
                  }
                  const options = statusFilterOptions.filter((s) => keys.includes(s.key)).map((s) => s.label);
                  return options.join(', ');
                }}
                inputProps={{ 'aria-label': 'Without label' }}
                style={{ borderRadius: '24px', height: '42px' }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      borderRadius: '24px',
                    },
                  },
                }}
              >
                {statusFilterOptions.map((statusOption) => (
                  <MenuItem key={statusOption.key} value={statusOption.key} sx={{ ml: !!statusOption.parentKey ? 3 : 0 }}>
                    <Checkbox checked={workOrderStatusFilter.indexOf(statusOption.key) > -1} />
                    <ListItemText primary={statusOption.label} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
        {/* Button */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
          <Stack direction="row" spacing={2}>
            <Button onClick={handleClose} variant="outlined" sx={{ borderRadius: '24px', fontFamily: 'Work Sans !important', textTransform: 'capitalize', color: '#39A2DB', borderColor: '#39A2DB' }}>
              Cancel
            </Button>
            <Button
              onClick={handleSearch}
              variant="contained"
              sx={{ borderRadius: '24px', fontFamily: 'Work Sans !important', textTransform: 'capitalize', color: 'white', backgroundColor: '#39A2DB', borderColor: '#39A2DB', width: '100px' }}
            >
              Search
            </Button>
          </Stack>
        </Box>
      </Popover>
    </Box>
  );
};

export default WorkOrdersTableHeader;
